/** Pure helpers between the Cotizador form and the backend body (spec 033). */

function pctToRatio(value) {
  if (value === null || value === undefined || value === '') return null;
  return Number(value) / 100;
}

/**
 * Builds the body of preview / save from the form. The form keeps
 * percentages as 0–100 for typing; the backend takes ratios (0–1).
 *
 * @param {object} form - Cotizador form state.
 * @param {{ pricingOnly?: boolean }} options - Preview skips the client fields.
 */
export function toQuoteBody(form, { pricingOnly = false } = {}) {
  const body = {
    countryIso: form.countryIso,
    line: form.line,
    channel: form.channel,
    modality: form.modality,
    assistanceProduct: form.line === 'asistencias' ? form.assistanceProduct : null,
    // Asistencias send stock and monthly sales; the backend prices stock + 12 × sales.
    quantity: form.line === 'asistencias' ? Math.max(1, projectedInsured(form)) : Number(form.quantity),
    stock: form.line === 'asistencias' ? Number(form.stock || 0) : null,
    monthlyNewSales: form.line === 'asistencias' ? Number(form.monthlyNewSales || 0) : null,
    employeeCount: form.line === 'employee_benefit' && form.quantity ? Number(form.quantity) : null,
    // Collective: memberships sold per employee (e.g. 2 pets each), billed whether they activate or not.
    membershipsPerEmployee: form.line === 'employee_benefit' ? Number(form.membershipsPerEmployee || 1) : null,
    // Employee Benefit always offers the Flexible plan too: a pool priced next to the collective one.
    poolSize: form.line === 'employee_benefit' && form.poolSize ? Number(form.poolSize) : null,
    // Direct sales carry no commission; the channel's own value (0) applies.
    commissionPct: form.channel === 'direct' ? null : pctToRatio(form.commissionPct),
    discountPct: pctToRatio(form.discountPct) ?? 0,
    lines: form.lines.map((line) => ({
      basePlan: line.basePlan,
      partnerVariantId: line.partnerVariantId || null,
      name: line.name || null,
      changes: line.changes
    }))
  };
  if (pricingOnly) return body;
  const isBroker = form.channel === 'broker';
  return {
    ...body,
    pipedriveOrgId: form.pipedriveOrgId || null,
    // A deal belongs to its organization: without one there is no deal.
    pipedriveDealId: form.pipedriveOrgId ? form.pipedriveDealId || null : null,
    // "+ Crear trato nuevo": the pipeline where generating the proposal creates the deal.
    pipedriveNewDealPipelineId:
      form.pipedriveOrgId && !form.pipedriveDealId && form.newDeal ? form.pipedriveNewDealPipelineId || null : null,
    clientName: form.clientName.trim(),
    brokerId: isBroker ? form.brokerId : null,
    brokerName: isBroker ? form.brokerName : null,
    notes: form.notes ? form.notes.trim() : null
  };
}

/**
 * Applies changes to a plan's items the way the backend does: replace or add
 * by service, remove with `eventsPerYear: 0`.
 *
 * @param {Array<{serviceCode: string, eventsPerYear: number, cap?: number}>} items
 * @param {Array<{serviceCode: string, eventsPerYear: number, cap?: number}>} changes
 */
export function applyChanges(items, changes) {
  const result = items.map((item) => ({ ...item }));
  for (const change of changes) {
    const index = result.findIndex((item) => item.serviceCode === change.serviceCode);
    if (change.eventsPerYear === 0) {
      if (index >= 0) result.splice(index, 1);
    } else if (index >= 0) {
      result.splice(index, 1, { ...change });
    } else {
      result.push({ ...change });
    }
  }
  return result;
}

/** Insured persons expected in 12 months: stock + 12 × monthly new sales. */
export function projectedInsured(form) {
  return Number(form.stock || 0) + 12 * Number(form.monthlyNewSales || 0);
}

/** Recommended Flexible pool: a share of the employees, rounded up. */
export function recommendedPool(employees, pct) {
  if (!employees || !pct) return null;
  return Math.ceil((Number(employees) * Number(pct)) / 100);
}

/**
 * Plans sent to price. A quote always offers the three standard plans side by
 * side, except when only Virtual can be sold (under the minimum memberships
 * for coverage plans, or a Virtual-only alliance).
 *
 * @param {Array<{basePlan: string}>} plans - The three plans of the form.
 * @param {boolean} onlyVirtual
 */
export function plansToQuote(plans, onlyVirtual) {
  if (!onlyVirtual) return plans;
  return plans.filter((plan) => plan.basePlan === 'virtual');
}

/**
 * Splits the saved lines of a quote into the three standard plans and the
 * custom ones. A standard plan is a line with no name, variant or change; a
 * standard plan missing from the saved lines comes back untouched.
 *
 * @param {Array<object>} saved - `input.lines` of a saved quote.
 * @param {Array<object>} standard - The three standard plans of an empty form.
 */
export function splitSavedLines(saved, standard) {
  const isStandard = (line) => !line.name && !line.partnerVariantId && line.changes.length === 0;
  const custom = [];
  const found = new Map();
  for (const line of saved) {
    if (isStandard(line) && !found.has(line.basePlan)) found.set(line.basePlan, line);
    else custom.push({ ...line, name: line.name || null, changes: [...line.changes] });
  }
  return {
    standard: standard.map((plan) => (found.has(plan.basePlan) ? { ...plan } : plan)),
    custom
  };
}

/** A plan name not yet in `taken`: adds " 2", " 3"… when it repeats. */
export function uniquePlanName(name, taken) {
  if (!taken.includes(name)) return name;
  let n = 2;
  while (taken.includes(`${name} ${n}`)) n += 1;
  return `${name} ${n}`;
}
